import { Alert, Button, ButtonProps, Chip, Stack, Typography } from '@mui/material';
import { useRouter } from '@/hooks';
import { useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ZoneStatusList } from '../ZoneStatusTimeline/ZoneStatusList';
import { ZoneStatus, useUpdateZoneStatusMutation, History, Zone } from '@/__generated__/graphql';
import { Container } from './styles';
import { shouldRedirectToTasks, ZoneStatusMapper } from '../common';
import { ZoneStatusChangerOptions } from './ZoneStatusChangerOptions';
import * as state from '@/state';
import { abyssColors } from '@/theme/colors';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { AssigneeAvatar } from '@/components/ZonesManagementRevamped/AssigneeAvatar';

const infoButtonProps: ButtonProps = {
  variant: 'contained',
  color: 'info',
};

type Props = {
  status?: ZoneStatus;
  histories?: History[];
};

const getZoneStatusUpdateInput = (status: ZoneStatus, zoneId: string) => {
  return {
    variables: {
      input: {
        zoneId,
        status,
      },
    },
  };
};

const ErrorAlert = (
  <div>
    <Alert severity="error">Error updating zone status</Alert>
  </div>
);

const SuccessAlert = (
  <div>
    <Alert severity="success">Zone status updated</Alert>
  </div>
);

export const ZoneStatusChanger = ({ histories }: Props) => {
  const router = useRouter();
  const zoneIdFromUrl = router?.query?.zoneid;
  const [selectedZone, setSelectedZone] = useAtom(state.selectedZone);
  const zoneId = zoneIdFromUrl ? zoneIdFromUrl.toString() : selectedZone?.id;
  const inspection = router?.query?.inspection as string;
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const zoneHistories = useAtomValue(state.zoneHistories);
  const currentZoneHistory = zoneHistories.find((item) => item.id === zoneId);
  const assignedUser = selectedZone?.workflowStatus?.assignedToUser;
  const picture = assignedUser?.picture;
  const name = assignedUser?.name;
  const [mutate] = useUpdateZoneStatusMutation({
    onCompleted: (data) => {
      if (shouldRedirectToTasks(selectedZone?.status, data.updateZoneStatus.currentStatus)) {
        router.push({
          pathname: `/inspection/${inspection}/tasks`,
        });
        return;
      }
      setSelectedZone(
        (previous) =>
          ({
            ...previous,
            status: data.updateZoneStatus.currentStatus,
          }) as Zone
      );
    },
    refetchQueries: ['zonesAndHistoriesFor'],
  });

  const updateZoneStatus = useCallback(
    (input: ZoneStatus) => {
      if (!zoneId) {
        setSnackbarMessage({
          shouldShow: true,
          content: ErrorAlert,
        });
        return;
      }

      mutate(getZoneStatusUpdateInput(input, zoneId))
        .then(() => {
          setSnackbarMessage({
            shouldShow: true,
            content: SuccessAlert,
          });
        })
        .catch(() => {
          setSnackbarMessage({
            shouldShow: true,
            content: ErrorAlert,
          });
        });
    },
    [mutate, setSnackbarMessage, zoneId]
  );

  const StartButton = useCallback(() => {
    return (
      <Button
        sx={{
          backgroundColor: abyssColors.success[500],
          width: '7.1rem',
          height: '2.6rem',
          padding: '0.4rem 0.8rem 0.4rem 0.8rem',
        }}
        {...infoButtonProps}
      >
        <AbyssIcon
          iconName="play"
          style={{
            height: '1.6rem',
            width: '1.6rem',
          }}
        ></AbyssIcon>
        <Typography sx={{ fontSize: '1.2rem', marginLeft: '0.6rem', marginRight: '0.6rem' }}>
          Start
        </Typography>
      </Button>
    );
  }, []);

  const ApproveButton = useCallback(() => {
    const handleClick = () => {
      updateZoneStatus(ZoneStatus.Completed);
    };

    return (
      <Button variant="contained" color="success" onClick={handleClick}>
        Approve
      </Button>
    );
  }, [updateZoneStatus]);

  const RejectButton = useCallback(() => {
    const handleClick = () => {
      updateZoneStatus(ZoneStatus.NotStarted);
    };

    return (
      <Button variant="contained" color="error" onClick={handleClick}>
        Reject
      </Button>
    );
  }, [updateZoneStatus]);

  const selectButton = (currentStatus: ZoneStatus) => {
    switch (currentStatus) {
      case ZoneStatus.NotStarted:
        return <StartButton />;
      case ZoneStatus.InProgress:
        return (
          <Stack direction="row" spacing={1}>
            <ApproveButton />
            <RejectButton />
          </Stack>
        );
      default:
        return <StartButton />;
    }
  };

  if (!selectedZone || !selectedZone?.status) return <></>;

  const { status } = selectedZone;
  return (
    <Container>
      <AssigneeAvatar
        sx={{ width: '400px', height: '40px' }}
        zoneId={selectedZone.id}
        assigneeName={name ?? undefined}
        profileImage={picture ?? undefined}
      />
      <Chip label={ZoneStatusMapper[status]} />

      <ZoneStatusList
        status={status}
        histories={histories || currentZoneHistory?.histories || []}
      />
      <div>{selectButton(status)}</div>
      <ZoneStatusChangerOptions />
    </Container>
  );
};
